import { useSpaUrl } from "@serophin/browser";
import { type CollectionDto, DEFAULT_COLLECTION_ID } from "@serophin/nottldr-shared";
import { useCollections } from "@serophin/nottldr-shared/hooks";
import { Loader2 } from "lucide-preact";
import { useEffect, useState } from "preact/hooks";
import { ManageCollections } from "../../components/collections/ManageCollections";
import { ViewCollections } from "../../components/collections/ViewCollections";
import { HeaderTab } from "../../components/explore/HeaderTab";
import { useHeaderActions } from "../../hooks/useHeaderActions";
import type { PageProps } from "../pageProps";
import { useRequireSession } from "./useRequireSession";

type Tab = "view" | "manage";

const useCollectionTabs = (active: Tab): void => {
  useHeaderActions(
    () => (
      <div class="flex items-end h-full">
        <HeaderTab href="/collections" isActive={active === "view"}>
          Collections
        </HeaderTab>
        <HeaderTab href="/collections/manage" isActive={active === "manage"}>
          Manage
        </HeaderTab>
      </div>
    ),
    [active],
  );
};

const Loading = () => (
  <div class="pt-2">
    <div class="flex justify-center items-center h-full">
      <Loader2 size={36} class="animate-spin mr-2 text-gray-500" />
      <div class="text-lg text-gray-500">Loading collections...</div>
    </div>
  </div>
);

const idFromUrl = (): string => new URLSearchParams(window.location.search).get("id") || DEFAULT_COLLECTION_ID;

/** The selected collection lives in `?id=`; picking another pushes the url without a navigation, and Back follows it. */
const useSelectedId = (): [string, (collection: CollectionDto) => void] => {
  const url = useSpaUrl();
  const [selected, setSelected] = useState(idFromUrl);

  // A link to another collection keeps this page mounted, so the router's url is what says it changed.
  useEffect(() => setSelected(idFromUrl()), [url]);

  useEffect(() => {
    const onPopState = () => setSelected(idFromUrl());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const select = (collection: CollectionDto) => {
    setSelected(collection.id);
    window.history.pushState({}, "", collection.isDefault ? "/collections" : `/collections?id=${collection.id}`);
  };

  return [selected, select];
};

const View = ({ collections }: { collections: CollectionDto[] }) => {
  const [selected, select] = useSelectedId();
  const known = collections.some((collection) => collection.id === selected);
  return <ViewCollections selectedId={known ? selected : DEFAULT_COLLECTION_ID} collections={collections} onSelect={select} />;
};

const CollectionsTab = ({ tab }: { tab: Tab }) => {
  const { collections, isLoading } = useCollections();
  useCollectionTabs(tab);
  if (isLoading) {
    return <Loading />;
  }
  return tab === "view" ? <View collections={collections} /> : <ManageCollections collections={collections} />;
};

/** `/collections`: the articles saved in one collection. */
export const CollectionsPage = (_props: PageProps) => (useRequireSession() ? <CollectionsTab tab="view" /> : null);

/** `/collections/manage`: create, rename, copy and delete. */
export const ManageCollectionsPage = (_props: PageProps) => (useRequireSession() ? <CollectionsTab tab="manage" /> : null);
